// Native WebSocket wrapper replacing Socket.io
// Provides similar emit/on API for minimal client code changes

const WS_URL = process.env.REACT_APP_WS_URL || (
  process.env.NODE_ENV === 'production'
    ? 'wss://songbattle-worker.richard-heiland6.workers.dev/ws'
    : 'ws://localhost:8787/ws'
);

class GameSocket {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.pendingCallbacks = new Map();
    this.reqCounter = 0;
    this.connected = false;
    this.playerId = null;
    this.roomCode = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000;
    this._connectParams = null;
  }

  // Connect to create or join a room
  connect(params = {}) {
    // params: { action: 'create'|'join', room: 'CODE', name: 'PlayerName' }
    this._connectParams = params;
    const queryParts = [];
    if (params.action) queryParts.push(`action=${params.action}`);
    if (params.room) queryParts.push(`room=${params.room.toUpperCase()}`);
    if (params.name) queryParts.push(`name=${encodeURIComponent(params.name)}`);

    const url = `${WS_URL}${queryParts.length ? '?' + queryParts.join('&') : ''}`;

    this.ws = new WebSocket(url);
    this.ws.onopen = () => {
      this.connected = true;
      this.reconnectAttempts = 0;
      this._emit('connect');
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        this._handleMessage(msg);
      } catch (e) {
        console.error('WS message parse error:', e);
      }
    };

    this.ws.onclose = (event) => {
      this.connected = false;
      this._emit('disconnect');
      // Don't reconnect if kicked or lobby not found
      if (event.code !== 1000 && event.code !== 1008) {
        this._tryReconnect();
      }
    };

    this.ws.onerror = () => {
      // Error will trigger onclose
    };
  }

  _tryReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    this.reconnectAttempts++;
    setTimeout(() => {
      if (!this.connected && this._connectParams) {
        this.connect(this._connectParams);
      }
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  _handleMessage(msg) {
    const { type, data, reqId } = msg;

    // Handle request-response callbacks
    if (type.endsWith(':response') && reqId != null) {
      const callback = this.pendingCallbacks.get(reqId);
      if (callback) {
        this.pendingCallbacks.delete(reqId);
        callback(data);
      }
      return;
    }

    // Handle server-initiated events
    if (type === 'connected') {
      this.playerId = data.playerId;
      this.roomCode = data.roomCode;
      this._emit('connected', data);
      return;
    }

    // Broadcast event to listeners
    this._emit(type, data);
  }

  // Register event listener (like socket.on)
  on(event, handler) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(handler);
  }

  // Remove event listener (like socket.off)
  off(event, handler) {
    if (!handler) {
      this.listeners.delete(event);
    } else {
      const handlers = this.listeners.get(event) || [];
      this.listeners.set(event, handlers.filter(h => h !== handler));
    }
  }

  // Emit event to server with optional callback (like socket.emit)
  emit(type, data = {}, callback = null) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (callback) callback({ success: false, error: 'Not connected' });
      return;
    }

    const msg = { type, data };
    if (callback) {
      const reqId = ++this.reqCounter;
      msg.reqId = reqId;
      this.pendingCallbacks.set(reqId, callback);
      // Timeout after 10s
      setTimeout(() => {
        if (this.pendingCallbacks.has(reqId)) {
          this.pendingCallbacks.delete(reqId);
          callback({ success: false, error: 'Request timeout' });
        }
      }, 10000);
    }

    this.ws.send(JSON.stringify(msg));
  }

  disconnect() {
    if (this.ws) {
      this.ws.close(1000);
      this.ws = null;
    }
    this.connected = false;
    this.playerId = null;
    this.roomCode = null;
    this._connectParams = null;
  }

  _emit(event, data) {
    const handlers = this.listeners.get(event) || [];
    handlers.forEach(h => {
      try { h(data); } catch (e) { console.error(`Handler error for ${event}:`, e); }
    });
  }

  get id() {
    return this.playerId;
  }
}

// Singleton instance
const gameSocket = new GameSocket();
export default gameSocket;
